<template>
    <div class="fight-report-dice" :class="{'fight-report-dice--bare': bare}">
        <div class="fight-report-dice__label">{{ label }}</div>

        <div class="fight-report-dice__section-label">{{ $t('journey.fightReportDiceBreakdown.attackSectionLabel') }}</div>
        <div class="fight-report-dice__group">
            <template v-for="(die, index) in dice">
                <span v-if="index > 0" :key="'plus-' + index" class="fight-report-dice__plus">+</span>
                <span :key="'die-' + index" class="fight-report-dice__die">{{ die }}</span>
            </template>
            <template v-if="flatBonus !== 0">
                <span class="fight-report-dice__plus">{{ flatBonus > 0 ? '+' : '' }}</span>
                <span class="fight-report-dice__die fight-report-dice__die--bonus">{{ flatBonus }}</span>
            </template>
            <span class="fight-report-dice__eq">=</span>
            <span class="fight-report-dice__sum">{{ total }}</span>
        </div>

        <template v-if="hasBonusDmg">
            <div class="fight-report-dice__section-label">{{ $t('journey.fightReportDiceBreakdown.dmgSectionLabel') }}</div>
            <div class="fight-report-dice__group">
                <template v-for="(die, index) in bonusDice">
                    <span v-if="index > 0" :key="'bplus-' + index" class="fight-report-dice__plus">+</span>
                    <span :key="'bdie-' + index" class="fight-report-dice__die fight-report-dice__die--dmg">{{ die }}</span>
                </template>
                <template v-if="bonusFlat !== 0">
                    <span v-if="bonusDice.length" class="fight-report-dice__plus">+</span>
                    <span class="fight-report-dice__die fight-report-dice__die--dmg">{{ bonusFlat }}</span>
                </template>
                <span class="fight-report-dice__eq">=</span>
                <span class="fight-report-dice__sum fight-report-dice__sum--dmg">+{{ bonusDiceTotal + bonusFlat }}</span>
            </div>
        </template>

        <div v-if="$slots.notes" class="fight-report-dice__section-label">{{ $t('journey.fightReportDiceBreakdown.effectSectionLabel') }}</div>
        <slot name="notes" />
    </div>
</template>

<script>
export default {
    name: 'FightReportDiceBreakdown',
    props: {
        label: {type: String, required: true},
        dice: {type: Array, required: true},
        flatBonus: {type: Number, default: 0},
        total: {type: [Number, String], required: true},
        bonusDice: {type: Array, default: () => []},
        bonusFlat: {type: Number, default: 0},
        // Bez vlastniho pozadi/paddingu - pro pouziti uvnitr uz oramovaneho
        // popupu, kde by dve vrstvy pozadi na sobe vypadaly spatne.
        bare: {type: Boolean, default: false},
    },
    computed: {
        bonusDiceTotal() {
            // Kostky z API jsou stringy (Doctrine "simple_array") - bez
            // Number() by "+" konkatenoval retezce misto souctu.
            return this.bonusDice.reduce((sum, v) => sum + Number(v), 0);
        },
        hasBonusDmg() {
            return this.bonusDice.length > 0 || this.bonusFlat !== 0;
        },
    },
}
</script>

<style scoped>
.fight-report-dice {
    margin-top: 8px;
    border-radius: 8px;
    padding: 9px;
}
.theme--light .fight-report-dice:not(.fight-report-dice--bare) {
    background: rgba(0, 0, 0, 0.06);
}
.theme--dark .fight-report-dice:not(.fight-report-dice--bare) {
    background: rgba(0, 0, 0, 0.28);
}
.fight-report-dice--bare {
    margin-top: 0;
    padding: 0;
    color: #fff;
}
.fight-report-dice__label {
    font-size: 11px;
    margin-bottom: 7px;
}
.theme--light .fight-report-dice:not(.fight-report-dice--bare) .fight-report-dice__label {
    color: rgba(0, 0, 0, 0.6);
}
.theme--dark .fight-report-dice:not(.fight-report-dice--bare) .fight-report-dice__label,
.fight-report-dice--bare .fight-report-dice__label {
    color: rgba(255, 255, 255, 0.75);
}
.fight-report-dice__section-label {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.6;
    margin: 8px 0 4px;
}
.fight-report-dice__section-label:first-of-type {
    margin-top: 0;
}
.fight-report-dice__group {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
}
.fight-report-dice__die {
    width: 27px;
    height: 27px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 13px;
}
.theme--light .fight-report-dice:not(.fight-report-dice--bare) .fight-report-dice__die {
    background: rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.18);
}
.theme--dark .fight-report-dice:not(.fight-report-dice--bare) .fight-report-dice__die,
.fight-report-dice--bare .fight-report-dice__die {
    background: rgba(255, 255, 255, 0.09);
    border: 1px solid rgba(255, 255, 255, 0.18);
}
.fight-report-dice__die--dmg {
    color: #e0a53b;
    border-color: rgba(224, 165, 59, 0.4) !important;
    background: rgba(224, 165, 59, 0.18) !important;
}
.fight-report-dice__plus,
.fight-report-dice__eq {
    font-size: 12px;
    opacity: 0.6;
}
.fight-report-dice__sum {
    font-size: 16px;
    font-weight: 800;
    border-radius: 6px;
    padding: 2px 8px;
}
.theme--light .fight-report-dice:not(.fight-report-dice--bare) .fight-report-dice__sum {
    background: rgba(0, 0, 0, 0.1);
}
.theme--dark .fight-report-dice:not(.fight-report-dice--bare) .fight-report-dice__sum,
.fight-report-dice--bare .fight-report-dice__sum {
    background: rgba(255, 255, 255, 0.1);
}
.fight-report-dice__sum--dmg {
    color: #e0a53b;
    background: rgba(224, 165, 59, 0.18) !important;
}
</style>
